import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, QrCode, MessageSquare, Repeat, Users,
  Sparkles, TrendingUp, ShieldCheck, Check, Package, Gift, Store
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingChrome";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cms } from "@/lib/api";

const marketplaces = ["Shopee", "TikTok Shop", "Tokopedia", "Lazada", "Blibli", "Instagram", "WhatsApp", "Offline Store"];

const features = [
  { icon: QrCode, title: "QR Loyalty Card", desc: "Cetak kartu QR & masukkan ke paket marketplace. Pembeli scan → jadi database Anda.", color: "emerald" },
  { icon: Users, title: "Customer Database", desc: "Semua pelanggan dari Shopee, TikTok, Tokopedia jadi satu database milik brand Anda.", color: "blue" },
  { icon: MessageSquare, title: "WhatsApp Campaign", desc: "Kirim promo, reminder repeat order, dan winback dengan template siap pakai.", color: "green" },
  { icon: Repeat, title: "Repeat Order Engine", desc: "Deteksi pelanggan yang siap re-order berdasarkan siklus produk. Auto-reminder.", color: "amber" },
  { icon: Gift, title: "Cashback & Reward", desc: "Buat voucher, poin, dan cashback code. Tracking penggunaan otomatis.", color: "pink" },
  { icon: Sparkles, title: "AI Marketing Assistant", desc: "Generate pesan campaign, copy promo, script live selling — powered by Claude AI.", color: "purple" },
];

const steps = [
  { n: "01", title: "Buat QR Loyalty", desc: "Pilih reward (cashback/voucher/poin), atur produk & masa berlaku. Download kartu QR." },
  { n: "02", title: "Masukkan ke paket", desc: "Cetak kartu QR dan sisipkan di paket marketplace. Setiap pembeli akan melihatnya." },
  { n: "03", title: "Pembeli scan QR", desc: "Pelanggan mengisi nama & WhatsApp untuk klaim reward. Otomatis masuk database Anda." },
  { n: "04", title: "Kirim campaign", desc: "Gunakan WhatsApp Campaign Center untuk kirim reminder repeat order & promo eksklusif." },
];

export default function Landing() {
  const [content, setContent] = useState(null);
  useEffect(() => { cms.getLanding().then(setContent).catch(() => {}); }, []);
  const headline = content?.hero_headline || "Ubah pembeli marketplace jadi pelanggan milik brand Anda sendiri.";
  const subtitle = content?.hero_subtitle || "Marketplace untuk akuisisi. Custora untuk retensi. Bangun database pelanggan sendiri dari Shopee, TikTok Shop, Tokopedia, dan Lazada. Tingkatkan repeat order tanpa harus terus bakar iklan.";
  const ctaPrimary = content?.hero_cta_primary || "Coba Gratis 14 Hari";
  const ctaSecondary = content?.hero_cta_secondary || "Lihat Harga";

  return (
    <div className="min-h-screen bg-white">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-full px-3 py-1">
                <span className="mr-1.5">🌱</span> Customer Ownership Platform untuk UMKM
              </Badge>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 leading-[1.05]">
                {headline}
              </h1>
              <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
                {subtitle}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/signup">
                  <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 h-12 shadow-sm" data-testid="hero-cta-signup">
                    {ctaPrimary} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="border-gray-300 h-12" data-testid="hero-cta-pricing">{ctaSecondary}</Button>
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Tanpa kartu kredit</div>
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Setup 5 menit</div>
                <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Bahasa Indonesia</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-32 h-32 bg-emerald-100 rounded-full blur-3xl opacity-70" />
                <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-amber-100 rounded-full blur-3xl opacity-70" />

                {/* Mock dashboard card */}
                <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-5 z-10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Repeat Order Rate</div>
                      <div className="text-3xl font-bold text-gray-900 mt-0.5">48.2%</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                      <TrendingUp size={12} /> +23%
                    </div>
                  </div>
                  <div className="h-32 flex items-end gap-1.5">
                    {[30, 45, 38, 52, 44, 58, 48, 62, 55, 70, 65, 78].map((h, i) => (
                      <div key={`bar-${i}-${h}`} className="flex-1 bg-gradient-to-t from-emerald-500 to-emerald-300 rounded-t" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Customer database</span>
                      <span className="font-semibold text-gray-900">2,847</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Revenue campaign</span>
                      <span className="font-semibold text-emerald-600">Rp 18.4 juta</span>
                    </div>
                  </div>
                </div>

                {/* Floating QR card */}
                <div className="absolute -bottom-8 -left-6 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-20 rotate-[-6deg]">
                  <div className="w-24 h-24 bg-gray-900 rounded-lg flex items-center justify-center">
                    <QrCode className="text-white" size={64} strokeWidth={1.5} />
                  </div>
                  <div className="mt-2 text-[10px] font-semibold text-center text-gray-700">Scan untuk cashback</div>
                </div>

                {/* Floating notification */}
                <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-20 rotate-[4deg] max-w-[220px]">
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Gift className="text-amber-600" size={16} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-900">832 pelanggan siap reorder</div>
                      <div className="text-[10px] text-gray-500 mt-0.5">Estimasi revenue Rp 7-17 juta</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Marketplace marquee */}
          <div className="mt-16 lg:mt-24">
            <div className="text-center text-xs font-bold tracking-widest uppercase text-gray-400 mb-6">
              Bekerja dengan semua channel penjualan Anda
            </div>
            <Marquee gradient gradientColor="#ffffff" speed={40} pauseOnHover>
              {marketplaces.map((m) => (
                <div key={m} className="mx-8 text-2xl font-heading font-bold text-gray-300 hover:text-emerald-600 transition-colors">{m}</div>
              ))}
            </Marquee>
          </div>
        </div>
      </section>

      {/* PROBLEM STATEMENT */}
      <section className="bg-gray-50 border-y border-gray-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-emerald-600 mb-3">Masalahnya</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 leading-tight">
                Pembeli di Shopee, TikTok, & Tokopedia<br />
                <span className="text-red-500">bukan pelanggan Anda</span>.
              </h2>
              <p className="mt-5 text-gray-600 leading-relaxed">
                Setiap kali marketplace mengubah algoritma, biaya iklan naik, atau kompetitor kasih diskon lebih besar — Anda kehilangan mereka. Karena database itu milik marketplace, bukan brand Anda.
              </p>
            </div>
            <div className="space-y-3">
              {[
                "Tidak punya kontak WhatsApp pelanggan",
                "Repeat order rendah karena tidak bisa follow-up",
                "Biaya iklan makin mahal untuk buyer yang sama",
                "Sulit tahu produk mana yang benar-benar untung",
                "Tidak ada sistem loyalty, membership, atau referral",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
                  <div className="w-6 h-6 rounded-full bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-500 text-xs font-bold">×</span>
                  </div>
                  <div className="text-sm text-gray-700 font-medium">{t}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="fitur" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold tracking-widest uppercase text-emerald-600 mb-3">Solusinya</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
              Semua yang Anda butuhkan<br />untuk memiliki pelanggan sendiri.
            </h2>
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed">
              Dari akuisisi via QR loyalty di paket marketplace, sampai retensi via WhatsApp campaign otomatis. Semua di satu platform, dibuat khusus untuk UMKM Indonesia.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:border-emerald-200 hover:-translate-y-1 transition-all duration-200"
                >
                  <div className={`w-11 h-11 rounded-lg bg-${f.color}-50 flex items-center justify-center text-${f.color}-600 mb-4`}>
                    <Icon size={22} strokeWidth={2} />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-gray-900">{f.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="cara-kerja" className="bg-gray-900 text-white py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-2xl">
            <div className="text-xs font-bold tracking-widest uppercase text-emerald-400 mb-3">Cara Kerja</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              4 langkah sederhana<br />untuk mulai memiliki pelanggan.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s) => (
              <div key={s.n} className="border border-white/10 rounded-xl p-6 bg-white/[0.02] hover:bg-white/[0.05] transition-colors">
                <div className="font-heading text-4xl font-bold text-emerald-400">{s.n}</div>
                <h3 className="mt-4 font-heading text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimoni" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { name: "Sarah W.", role: "Owner @glowbaby.id", quote: "Repeat order rate naik dari 12% jadi 41% dalam 3 bulan. Database 4,200 pelanggan sekarang bukan milik Shopee, tapi milik brand kami." },
              { name: "Bagus S.", role: "Founder Kopi Sanggar", quote: "Nggak perlu bakar iklan mulu. WhatsApp campaign untuk reorder kopi tiap 14 hari sekarang jadi 30% dari revenue bulanan." },
              { name: "Rina M.", role: "CEO PetHouse Care", quote: "AI Marketing Assistant-nya nulis pesan WA lebih bagus dari saya. Conversion campaign winback 18%, dulu cuma 4%." },
            ].map((t) => (
              <div key={t.name} className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center font-heading font-bold text-emerald-700">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">"{t.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-emerald-600 rounded-2xl p-10 lg:p-16 overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-2xl">
                Mulai kumpulkan database pelanggan hari ini.
              </h2>
              <p className="mt-4 text-emerald-50 max-w-xl">
                Coba gratis 14 hari. Tanpa kartu kredit. Setup dalam 5 menit.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup">
                  <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold h-12 px-6" data-testid="footer-cta-signup">
                    Daftar Gratis Sekarang <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 h-12" data-testid="footer-cta-pricing">
                    Lihat Semua Fitur
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
